<template>
  <div>
    <div v-if="isAddingOrEditing" class="row">
      <div class="col-12 mt-3 ml-3">
        <icon-view
          class="p-1 mr-2 rounded-circle"
          :icon="titleIcon"
          icon-background="white"
          icon-color="#2EB882"
          icon-size="14px"
          :title="title"
        />
      </div>
    </div>
    <div class="p-4">
      <validation-observer :ref="addressFormRef" v-slot="{}" tag="form">
        <div class="row">
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              :name="'addressType'"
              rules="required"
            >
              <custom-drop-down
                v-model="addressTypeSelected"
                :clearable="!isPhysicalAddress"
                custom-error-msg="El tipo de dirección es requerida."
                :disabled="isPhysicalAddress"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Tipo de Dirección:"
                :options="addressTypesOptions"
                placeholder="Seleccione"
                @input="onChangeAddressType"
              />
            </validation-provider>
          </div>
        </div>
        <hr />
        <div class="row">
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="addressLine1"
              rules="required"
            >
              <label class="data-title mb-0">Dirección (Línea 1):</label>
              <custom-input
                v-model="model.addressLine1"
                :custom-error-msg="'La dirección (línea 1) es requerida.'"
                :error="errors[0]"
              />
            </validation-provider>
          </div>

          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="addressMunicipality"
              rules="required"
            >
              <label class="data-title mb-0">Municipio:</label>
              <custom-drop-down
                :id="'municipality'"
                v-model="municipalitySelected"
                custom-error-msg="El municipio es requerido."
                :disabled="isProviderOfficeOrSchool"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="municipalities"
                placeholder="Seleccione"
                @input="onChangeMunicipality"
              />
            </validation-provider>
          </div>
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="addressPostalCode"
              rules="required"
            >
              <custom-input
                v-model="model.zipCode"
                :custom-error-msg="'El código postal es requerido.'"
                :error="errors[0]"
                label="Código Postal:"
                mask-type="ZipcodeExt"
                placeholder="#####-####"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-4">
            <label class="data-title mb-0">Dirección (Línea 2):</label>
            <custom-input v-model="model.addressLine2" />
          </div>

          <div class="col-md-4">
            <label class="data-title mb-0">Latitud:</label>
            <custom-input
              :value="model.latitude"
              @input="(val) => (model.latitude = Number.parseFloat(val))"
            />
          </div>
          <div class="col-md-4">
            <label class="data-title mb-0">Longitud:</label>
            <custom-input
              :value="model.longitude"
              @input="(val) => (model.longitude = Number.parseFloat(val))"
            />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-4">
            <label class="data-title mb-0">Dirección (Línea 3):</label>
            <custom-input v-model="model.addressLine3" />
          </div>
          <div class="col-md-8">
            <label class="data-title mb-0">Anotaciones Adicionales:</label>
            <custom-input
              v-model="model.additionalAnnotations"
              placeholder="Escribir información adicional de como llegar..."
            />
          </div>
        </div>
      </validation-observer>

      <div v-if="isAddingOrEditing" class="row mt-5">
        <div class="col-12">
          <action-button
            class="float-right ml-2"
            i="fas fa-save fa-lg"
            text="Guardar"
            variant="success"
            @click="save"
          />
          <action-button
            class="float-right"
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger"
            @click="cancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import IconView from 'icon-view';
import AddressTypes from '@/utils/constants/addressTypes.js';
import CodeLocationTypes from '@/utils/constants/codeLocationTypes';
import { getMunicipalities as _getMunicipalities } from '@/services/common/location.api';

export default {
  name: 'LocationServiceAddress',
  components: {
    ValidationProvider,
    CustomDropDown,
    CustomInput,
    IconView,
    ActionButton,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    municipalities: {
      type: Array,
      default: () => [],
    },
    addressTypes: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      municipalitySelected: null,
      addressTypeSelected: null,
      model: { ...this.value },
    };
  },
  async mounted() {
    [...this.addressTypes].filter((x) => {
      if (x.id === AddressTypes.Physical) {
        this.model.addressTypeCd = x.id;
      }
    });

    this.addressTypeSelected.disabled = true;
  },
  beforeMount() {
    if (this.isProviderOfficeOrSchool) {
      this.getMunicipality();
    }
  },
  computed: {
    isEditing() {
      return Boolean(this.model.id);
    },
    isPhysicalAddress() {
      return this.model.addressTypeCd === AddressTypes.Physical;
    },
    isAddingOrEditing() {
      return this.isEditing || !this.isPhysicalAddress;
    },
    addressTypesOptions() {
      return [...this.addressTypes].filter((x) => x.id);
    },
    title() {
      const first = this.isEditing ? 'Editar' : 'Añadir';
      const last = this.isEditing ? this.model.addressTypeDescription : '';
      return `${first} Dirección ${last}`;
    },
    titleIcon() {
      return this.isEditing ? 'fas fa-edit' : 'fas fa-plus';
    },
    addressFormRef() {
      return `addressForm${this.model.id}`;
    },
    isProviderOfficeOrSchool() {
      return (
        this.data.demographicInfo.locationTypeId ===
          CodeLocationTypes.ProviderOffice ||
        this.data.demographicInfo.locationTypeId === CodeLocationTypes.School
      );
    },
  },
  methods: {
    onChangeMunicipality(item) {
      this.model.municipalityId = item?.id;
    },
    onChangeAddressType(item) {
      this.model.addressTypeCd = item.id;
    },
    setMunicipalitySelected(id) {
      if (this.municipalitySelected) return;
      if (!id) {
        this.municipalitySelected = null;
        return;
      }
      this.municipalitySelected = {
        id: id,
        name: this.value.municipalityDescription,
      };
    },
    setAddressTypeSelected(id) {
      if (this.addressTypeSelected) return;
      if (!id) {
        this.addressTypeSelected = null;
        return;
      }

      this.addressTypeSelected = this.addressTypes.find((y) => y.id === id);
    },
    async save() {
      let reff = 'addressForm' + this.model.id;
      const isFormValid = await this.$refs[reff].validate();
      if (isFormValid) this.$emit('save');
    },
    cancel() {
      this.$emit('cancel');
    },
    async getMunicipality() {
      const { data } = await _getMunicipalities({
        municipalityId: this.data.demographicInfo.municipalityId,
      });
      this.municipalitySelected = data[0];
    },
  },
  watch: {
    model: {
      deep: true,
      immediate: true,
      handler(value) {
        this.setMunicipalitySelected(value.municipalityId);
        this.setAddressTypeSelected(value.addressTypeCd);
        this.$emit('input', value);
      },
    },
  },
};
</script>

<style scoped lang="scss"></style>
